import cx from 'classnames';
import { FC, MouseEvent, useEffect, useRef, useState } from 'react';

import { Icon, Typography } from 'src/components-v2/DataDisplay';
import BtnTertiary from 'src/components-v2/Inputs/Button/BtnTertiary';
import { Link } from 'src/components/Inputs';
import { useTrack } from 'src/contexts/AnalyticsContext';
import { Events } from 'src/lib/trackingEvents';

import { Card } from '../Card';
import { Columns } from '../Columns';
import { RoundedButton } from '../RoundedButton';
import { Rows } from '../Rows';

import { NAV_LIST_DROPDOWN_HIDE_DELAY } from '../consts';
import {
  CardWIcon,
  CardWImage,
  isCardWImage,
  isRoundedButtonLink,
  Tabs as ItemsProps,
  TabsFeatured,
} from '../types';

import styles from './Tabs.module.scss';

type TabsProps = {
  className?: string;
  visible?: boolean;
  items: ItemsProps['children'];
  plainTabList?: boolean;
  featured?: TabsFeatured;
  tabListLink?: ItemsProps['tabListLink'];
  tabListGradient?: boolean;
};

export const Tabs: FC<TabsProps> = ({
  className,
  items,
  visible,
  plainTabList,
  featured,
  tabListLink,
  tabListGradient,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const track = useTrack();

  const handleFeaturedCtaClick = (event: MouseEvent<HTMLElement>) => {
    const anchor = (event.target as HTMLElement).closest('a');

    if (!anchor || !featured?.image.trackingId) {
      return;
    }

    track({
      type: Events.BUTTON_CLICKED,
      payload: {
        trackingId: Events.BUTTON_CLICKED,
        event: event as MouseEvent<HTMLAnchorElement>,
        currentTarget: anchor,
        buttonName: featured.image.trackingId,
        buttonHref: anchor.href,
      },
    });
  };

  const handleSetActiveIndex = (i: number) => {
    return (event) => {
      setActiveIndex(i);
    };
  };

  const setDefaultTab = () => {
    if (timer.current) {
      clearTimeout(timer.current);
    }

    timer.current = setTimeout(() => {
      setActiveIndex(0);
    }, NAV_LIST_DROPDOWN_HIDE_DELAY);
  };

  useEffect(() => {
    if (!visible) {
      setDefaultTab();
    }

    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, [visible]);

  return (
    <div
      className={cx(styles.Tabs, className, {
        [styles.Tabs_plain]: plainTabList,
        [styles.Tabs_withFeatured]: !!featured,
      })}
    >
      <div className={styles.Tabs__main}>
        <div
          className={cx(styles.Tabs__tabList, {
            [styles.Tabs__tabList_plain]: plainTabList,
            [styles.Tabs__tabList_gradient]: tabListGradient,
          })}
        >
          {items.map(({ id, title }, i) => {
            return (
              <div
                key={`main-menu-tabs-item-${id}`}
                className={styles.Tabs__tab}
              >
                <button
                  type='button'
                  tabIndex={-1}
                  className={cx(styles.Button, {
                    [styles.Button_active]: i === activeIndex,
                  })}
                  onClick={handleSetActiveIndex(i)}
                >
                  <Typography
                    theme='main-menu-new'
                    component='span'
                    variant='p'
                    sx={{
                      mb: 0,
                    }}
                  >
                    {title}
                  </Typography>
                  <div className={styles.Button__arrowBox}>
                    <Icon
                      icon='ArrowRight'
                      className={styles.Button__arrow}
                      sx={{ width: 14, height: 14 }}
                    />
                  </div>
                </button>
              </div>
            );
          })}

          {tabListLink && (
            <div className={styles.Tabs__tab}>
              <Link href={tabListLink.href}>
                <a className={styles.Button} tabIndex={-1}>
                  <Typography
                    theme='main-menu-new'
                    component='span'
                    variant='p'
                    sx={{
                      mb: 0,
                    }}
                  >
                    {tabListLink.title}
                  </Typography>
                  <div className={styles.Button__arrowBox}>
                    <Icon
                      icon='ArrowRight'
                      className={styles.Button__arrow}
                      sx={{ width: 14, height: 14 }}
                    />
                  </div>
                </a>
              </Link>
            </div>
          )}
        </div>

        <div className={styles.Tabs__contentStack}>
          {items.map((item, i) => {
            const { id, type, children } = item;
            const isActive = i === activeIndex;

            // Handle rows type
            if (type === 'rows') {
              return (
                <div
                  key={`main-menu-tabs-rows-${id}`}
                  className={cx(styles.Tabs__content, {
                    [styles.Tabs__content_active]: isActive,
                  })}
                >
                  <Rows items={children} />
                </div>
              );
            }

            // Handle columns type
            if (type === 'columns') {
              return (
                <div
                  key={`main-menu-tabs-columns-${id}`}
                  className={cx(styles.Tabs__content, {
                    [styles.Tabs__content_active]: isActive,
                  })}
                >
                  <Columns items={children} />
                </div>
              );
            }

            // Handle list type (default)
            const isLargeGrid = children.some((child) => {
              return isCardWImage(child);
            });

            const nonRoundedCount = children.filter((child) => {
              return !isRoundedButtonLink(child);
            }).length;
            const roundedButtonSpansFull =
              nonRoundedCount > 0 && nonRoundedCount % 2 === 0;

            return (
              <ul
                key={`main-menu-tabs-list-${id}`}
                className={cx(styles.Tabs__list, {
                  [styles.Tabs__list_active]: isActive,
                  [styles.Tabs__list_large]: isLargeGrid,
                  [styles.Tabs__list_compact]: item.compact,
                })}
              >
                {children.map((child, j) => {
                  const isOverview = 'isOverview' in child && child.isOverview;

                  if (isRoundedButtonLink(child)) {
                    return (
                      <li
                        key={`main-menu-tab-list-item-${id}-${j}`}
                        className={cx(styles.Tabs__item, {
                          [styles.itemFullWidth]: roundedButtonSpansFull,
                        })}
                      >
                        <RoundedButton
                          {...child}
                          tabIndex={-1}
                          data-hidden={!isActive}
                          fullWidth={roundedButtonSpansFull}
                        />
                      </li>
                    );
                  }

                  const cardChild = child as
                    | (CardWIcon & { isOverview?: boolean })
                    | CardWImage;

                  return (
                    <li
                      key={`main-menu-tab-list-item-${id}-${j}`}
                      className={cx(styles.Tabs__item, {
                        [styles.Tabs__item_large]: isOverview,
                      })}
                    >
                      <Card
                        className={styles.Card}
                        {...cardChild}
                        iconBoxSize={40}
                        tabIndex={-1}
                        data-hidden={!isActive}
                      />
                    </li>
                  );
                })}
              </ul>
            );
          })}
        </div>
      </div>

      {featured && (
        <div className={styles.Tabs__featured}>
          {featured.title && (
            <Typography
              className={styles.Tabs__featuredTitle}
              theme='main-menu-new'
              variant='h6'
            >
              {featured.title}
            </Typography>
          )}
          <Card {...featured.image} cta={undefined} tabIndex={-1} />
          {featured.image.cta && (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
            <div onClick={handleFeaturedCtaClick}>
              <BtnTertiary
                variant='tertiary'
                href={featured.image.href}
                tabIndex={-1}
              >
                {featured.image.cta}
              </BtnTertiary>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
